import { SAFETY_TABLE_3 } from "./constants";

// ─── Safety Warnings ──────────────────────────────────────────
export function checkSafetyWarnings(protocol) {
  const warnings = [];
  const freq = protocol.frequency_hz;
  const ratio = protocol.treatment_ratio_pct;
  const trainPulses = protocol.train_pulses;

  // Table 3: Check train duration
  if (freq && trainPulses && ratio && SAFETY_TABLE_3[freq]) {
    const trainDurationSec = trainPulses / freq;
    const roundedRatio = Math.round(ratio / 10) * 10;
    const maxDuration = SAFETY_TABLE_3[freq]?.[roundedRatio];
    if (maxDuration && trainDurationSec > maxDuration) {
      warnings.push(
        `Train duration (${trainDurationSec.toFixed(1)}s) exceeds Table 3 maximum safe duration ` +
        `(${maxDuration}s) for ${freq} Hz at ${roundedRatio}% MT.`
      );
    }
  }

  // Table 4: ITI safety for ≤20 Hz
  if (freq && freq <= 20 && protocol.inter_train_interval_sec !== undefined && ratio) {
    const iti = protocol.inter_train_interval_sec * 1000; // convert to ms
    const roundedRatio = Math.round(ratio / 10) * 10;
    if (roundedRatio >= 100 && iti <= 5000) {
      if (roundedRatio >= 120) {
        warnings.push(
          `Inter-train interval (${protocol.inter_train_interval_sec}s) may be unsafe at ${roundedRatio}% MT per Table 4 safety recommendations.`
        );
      } else if (iti <= 1000) {
        warnings.push(
          `Inter-train interval (${protocol.inter_train_interval_sec}s) may be unsafe at ${roundedRatio}% MT per Table 4 safety recommendations.`
        );
      }
    }
  }

  return warnings;
}

// ─── Protocol Calculations ────────────────────────────────────
export function calcTotalPulses(p) {
  if (p.treatment_type === "TBS" && p.pulses_in_burst) {
    return (p.pulses_in_burst || 3) * (p.train_pulses || 0) * (p.num_trains || 0);
  }
  return (p.train_pulses || 0) * (p.num_trains || 0);
}

export function calcDuration(p) {
  const freq = p.frequency_hz || 1;
  const trainDur = (p.train_pulses || 0) / freq;
  const totalTrainTime = trainDur * (p.num_trains || 0);
  const totalITI = (p.inter_train_interval_sec || 0) * ((p.num_trains || 1) - 1);
  return totalTrainTime + totalITI;
}

export function formatDuration(sec) {
  if (!sec || sec <= 0) return "0s";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}

// ─── Permission Helpers ───────────────────────────────────────
export function canDo(user, permission) {
  if (!user) return false;
  if (user.role === "MD") return true;
  return user.permissions?.[permission] === true;
}

// ─── Common Formatters ───────────────────────────────────────

/** Return local date as YYYY-MM-DD string (never UTC-shifted) */
export function localDateStr(date) {
  const d = date || new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Return local datetime as ISO-like string YYYY-MM-DDTHH:MM:SS (no Z, no UTC shift) */
export function localISOString(date) {
  const d = date || new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
}

export function formatDate(d) {
  if (!d) return "—";
  const dt = new Date(d + (d.includes("T") ? "" : "T00:00:00"));
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const yyyy = dt.getFullYear();
  if (isNaN(yyyy)) return "—";
  return `${mm}/${dd}/${yyyy}`;
}

export function getUserName(users, id) {
  const u = users.find((usr) => usr.id === id);
  return u ? `${u.first_name} ${u.last_name}` : "—";
}
